import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Download, FileText } from "lucide-react";
import { api, API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrency, fmtDate, fmtPct } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const fmtSize = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${(b / 1e3).toFixed(0)} KB`);

function Stat({ label, value }) {
  return (
    <div className="p-4">
      <p className="text-xs uppercase text-muted-foreground">{label}</p>
      <p className="font-display text-xl tabular-nums mt-1">{value}</p>
    </div>
  );
}

export default function AccountantClientDetail() {
  const { clientId } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(String(currentYear));

  const { data: clients = [] } = useQuery({
    queryKey: ["accountant-clients"],
    queryFn: async () => (await api.get("/accountant/clients")).data,
  });
  const client = clients.find((c) => c.client_id === clientId);

  const { data: dashboard } = useQuery({
    queryKey: ["accountant-dashboard", clientId],
    queryFn: async () => (await api.get(`/accountant/clients/${clientId}/dashboard`)).data,
  });
  const { data: properties = [] } = useQuery({
    queryKey: ["accountant-properties", clientId],
    queryFn: async () => (await api.get(`/accountant/clients/${clientId}/properties`)).data,
  });
  const { data: expenses = [] } = useQuery({
    queryKey: ["accountant-expenses", clientId],
    queryFn: async () => (await api.get(`/accountant/clients/${clientId}/expenses`)).data,
  });
  const { data: documents = [] } = useQuery({
    queryKey: ["accountant-documents", clientId],
    queryFn: async () => (await api.get(`/accountant/clients/${clientId}/documents`)).data,
  });
  const { data: taxReport } = useQuery({
    queryKey: ["accountant-tax-report", clientId, year],
    queryFn: async () => (await api.get(`/accountant/clients/${clientId}/tax-report`, { params: { year } })).data,
  });

  const yearOptions = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

  return (
    <div className="space-y-6" data-testid="accountant-client-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/accountant/clients")} data-testid="back-to-clients">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="font-display text-3xl">{client?.name || "…"}</h1>
          <p className="text-sm text-muted-foreground">{client?.email}</p>
        </div>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">{t("nav_dashboard")}</TabsTrigger>
          <TabsTrigger value="properties" data-testid="tab-properties">{t("nav_properties")}</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">{t("nav_expenses")}</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">{t("nav_documents")}</TabsTrigger>
          <TabsTrigger value="tax" data-testid="tab-tax-report">{t("accountant_tax_report")}</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <Card className="rounded-sm border-border overflow-hidden grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-border">
            <Stat label={t("monthly_income")} value={fmtCurrency(dashboard?.monthly_income)} />
            <Stat label={t("net_monthly")} value={fmtCurrency(dashboard?.net_monthly)} />
            <Stat label={t("portfolio_value")} value={fmtCurrency(dashboard?.portfolio_value)} />
            <Stat label={t("gross_yield")} value={fmtPct(dashboard?.gross_yield)} />
            <Stat label={t("net_yield")} value={fmtPct(dashboard?.net_yield)} />
            <Stat label={t("occupancy")} value={fmtPct(dashboard?.occupancy)} />
            <Stat label={t("properties")} value={dashboard?.properties_count ?? "—"} />
            <Stat label={t("tenants")} value={dashboard?.tenants_count ?? "—"} />
          </Card>
        </TabsContent>

        <TabsContent value="properties">
          <Card className="rounded-sm border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("property")}</TableHead>
                  <TableHead>{t("address")}</TableHead>
                  <TableHead className="text-right">{t("purchase_price")}</TableHead>
                  <TableHead className="text-right">{t("current_value")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {properties.map((p) => (
                  <TableRow key={p.id} data-testid={`accountant-property-${p.id}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell>{p.address}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtCurrency(p.purchase_price)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtCurrency(p.current_value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="expenses">
          <Card className="rounded-sm border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("category")}</TableHead>
                  <TableHead>{t("description")}</TableHead>
                  <TableHead>{t("date")}</TableHead>
                  <TableHead className="text-right">{t("amount")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((e) => (
                  <TableRow key={e.id} data-testid={`accountant-expense-${e.id}`}>
                    <TableCell><Badge variant="secondary" className="rounded-sm">{e.category}</Badge></TableCell>
                    <TableCell>{e.description}</TableCell>
                    <TableCell>{fmtDate(e.date)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtCurrency(e.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card className="rounded-sm border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("filename")}</TableHead>
                  <TableHead>{t("category")}</TableHead>
                  <TableHead className="text-right">{t("size")}</TableHead>
                  <TableHead>{t("uploaded")}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((d) => (
                  <TableRow key={d.id} data-testid={`accountant-doc-${d.id}`}>
                    <TableCell className="font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" /> {d.original_filename}
                    </TableCell>
                    <TableCell><Badge variant="secondary" className="rounded-sm">{d.category}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums">{fmtSize(d.size)}</TableCell>
                    <TableCell>{fmtDate(d.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon"
                        onClick={() => window.open(`${API}/accountant/clients/${clientId}/documents/${d.id}/download`, "_blank")}
                        data-testid={`accountant-download-doc-${d.id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="tax" className="space-y-4">
          <div className="flex items-center justify-between">
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-32 rounded-sm" data-testid="tax-report-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" className="rounded-sm gap-2"
              onClick={() => window.open(`${API}/accountant/clients/${clientId}/tax-report/export?year=${year}`, "_blank")}
              data-testid="tax-report-export-btn">
              <Download className="h-4 w-4" /> {t("accountant_export_csv")}
            </Button>
          </div>

          <Card className="rounded-sm border-border overflow-hidden grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
            <Stat label={t("accountant_gross_income")} value={fmtCurrency(taxReport?.gross_income)} />
            <Stat label={t("accountant_deductible_expenses")} value={fmtCurrency(taxReport?.deductible_expenses)} />
            <Stat label={t("accountant_net_income_estimate")} value={fmtCurrency(taxReport?.net_income_estimate)} />
          </Card>

          {taxReport?.properties?.length > 0 && (
            <Card className="rounded-sm border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("property")}</TableHead>
                    <TableHead className="text-right">{t("accountant_gross_income")}</TableHead>
                    <TableHead className="text-right">{t("accountant_deductible_expenses")}</TableHead>
                    <TableHead className="text-right">{t("accountant_net_income_estimate")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {taxReport.properties.map((p) => (
                    <TableRow key={p.property_id}>
                      <TableCell className="font-medium">{p.property_name}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtCurrency(p.gross_income)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtCurrency(p.deductible_expenses)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtCurrency(p.net_income)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}

          <p className="text-xs text-muted-foreground">{taxReport?.disclaimer}</p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
